import { uploadLiveLog } from "../utils/log";

export const CAPTCHA_APP_ID = "194976309";
export const CAPTCHA_SCRIPT_URL = "https://turing.captcha.qcloud.com/TJCaptcha.js";
export const CAPTCHA_DISASTER_ERROR = {
  JS_LOAD_ERROR: {
    code: 1001,
    message: "jsload_error",
  },
};

export const createCaptchaDisasterResult = ({
  appId = CAPTCHA_APP_ID,
  errorCode = CAPTCHA_DISASTER_ERROR.JS_LOAD_ERROR.code,
  errorMessage = CAPTCHA_DISASTER_ERROR.JS_LOAD_ERROR.message,
  now = Math.floor(Date.now() / 1000),
  randstr = `@${Math.random().toString(36).slice(2)}`,
} = {}) => ({
  ret: 0,
  randstr,
  ticket: `trerror_${errorCode}_${appId}_${now}`,
  errorCode,
  errorMessage,
});

/// 验证码结果是否异常
export const isCaptchaDisasterResult = (res) => {
  return !!(res && res.ticket && /^trerror_/.test(res.ticket));
};

// 全局单例加载 Promise，防止多次注入
let captchaLoadingPromise = null;

/**
 * 健壮的脚本加载器：支持检测已存在的标签、全局变量，并提供 Promise 封装
 */
const loadCaptchaScript = (url = CAPTCHA_SCRIPT_URL) => {
  if (captchaLoadingPromise) return captchaLoadingPromise;

  captchaLoadingPromise = new Promise((resolve, reject) => {
    // 1. 如果全局变量已存在，直接成功
    if (window.TencentCaptcha) {
      resolve();
      return;
    }

    // 2. 检查 DOM 中是否已有相同 URL 的脚本标签（可能是 main.js 或 index.html 注入的）
    const existingScript = document.querySelector(`script[src="${url}"]`);
    if (existingScript) {
      // 如果脚本已存在但变量还没好，轮询等待其加载完成
      let pollCount = 0;
      const pollTimer = setInterval(() => {
        pollCount++;
        if (window.TencentCaptcha) {
          clearInterval(pollTimer);
          resolve();
        } else if (pollCount > 100) {
          // 10秒超时
          clearInterval(pollTimer);
          captchaLoadingPromise = null; // 失败后允许重试
          reject(new Error("jsload_error"));
        }
      }, 100);
      return;
    }

    // 3. 真正需要动态注入的情况
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = url;
    script.async = true;
    script.onload = () => {
      if (window.TencentCaptcha) {
        resolve();
      } else {
        captchaLoadingPromise = null;
        reject(new Error("jsload_error"));
      }
    };
    script.onerror = () => {
      captchaLoadingPromise = null; // 允许下次尝试其他 URL
      reject(new Error("jsload_error"));
    };
    document.head.appendChild(script);
  });

  return captchaLoadingPromise;
};

export default {
  data() {
    return {
      captcha: null,
      showCaptchaLoading: false,
    };
  },
  methods: {
    /**
     * 初始化验证码
     * 逻辑：优先检测全局变量 -> 轮询等待全局脚本 -> 动态注入主脚本 -> 动态注入备用脚本
     */
    async initCaptcha() {
      if (window.TencentCaptcha) {
        uploadLiveLog({
          message: "人机验证脚本-已经存在TencentCaptcha",
          module: "mixin",
          level: "info",
          tag: "pageLoad",
          extra: { component: "ManMachineVerify" },
        });
        return this._createCaptchaInstance();
      }

      try {
        // 尝试等待或加载主脚本
        await loadCaptchaScript(CAPTCHA_SCRIPT_URL);
        const created = this._createCaptchaInstance();
        uploadLiveLog({
          message: "人机验证脚本-主脚本加载/获取成功",
          module: "mixin",
          level: "info",
          tag: "pageLoad",
          extra: { component: "ManMachineVerify" },
        });
        return created;
      } catch (err) {
        uploadLiveLog(
          {
            message: "人机验证脚本-加载失败",
            module: "mixin",
            level: "warn",
            tag: "pageLoad",
            extra: { component: "ManMachineVerify", error: err.message },
          },
          true
        );
        return false;
      }
    },

    _createCaptchaInstance() {
      if (this.captcha) {
        return true;
      }
      if (window.TencentCaptcha && !this.captcha) {
        try {
          this.captcha = new window.TencentCaptcha(
            CAPTCHA_APP_ID,
            this._captchaCallback,
            {}
          );
          return true;
        } catch (error) {
          uploadLiveLog(
            {
              message: "人机验证脚本-实例化失败",
              module: "mixin",
              level: "warn",
              tag: "api",
              extra: {
                component: "ManMachineVerify",
                error: error?.message || String(error),
              },
            },
            true
          );
        }
      }
      return false;
    },

    _captchaCallback(res) {
      this.showCaptchaLoading = false;
      uploadLiveLog({
        message: "人机验证脚本-回调",
        module: "mixin",
        level: "info",
        tag: "api",
        extra: {
          component: "ManMachineVerify",
          res: res,
        },
      });
      if (res?.ret === 2) {
        uploadLiveLog({
          message: "人机验证脚本-用户取消",
          module: "mixin",
          level: "info",
          tag: "userAction",
          extra: { component: "ManMachineVerify" },
        });
        return;
      }
      if (res?.ret === 0 && res.ticket) {
        this.captchaCallback(res);
        return;
      }
      this.$message?.error?.("验证失败，请稍后重试");
    },

    async reloadCaptcha() {
      this.captcha = null;
      await this.initCaptcha();
    },

    _handleCaptchaDisaster(error, meta = {}) {
      this.showCaptchaLoading = false;
      const res = createCaptchaDisasterResult({
        errorCode: meta.errorCode,
        errorMessage: meta.errorMessage,
      });

      uploadLiveLog(
        {
          message: "人机验证脚本-容灾票据生成",
          module: "mixin",
          level: "warn",
          tag: "api",
          extra: {
            component: "ManMachineVerify",
            stage: meta.stage,
            error: error?.message || String(error),
            errorCode: res.errorCode,
            ticketPrefix: "trerror",
          },
        },
        true
      );

      this.captchaCallback(res);
      return res;
    },

    /**
     * 显示验证码，具备自动补救加载逻辑
     * 开发模式下跳过人机验证
     */
    async showCaptcha() {
      try {
        this.showCaptchaLoading = true;

        if (!this.captcha) {
          await this.initCaptcha();
        }

        if (this.captcha) {
          try {
            this.captcha.show();
          } catch (error) {
            return this._handleCaptchaDisaster(error, {
              stage: "show",
              errorCode: CAPTCHA_DISASTER_ERROR.JS_LOAD_ERROR.code,
              errorMessage: CAPTCHA_DISASTER_ERROR.JS_LOAD_ERROR.message,
            });
          }
          return true;
        }

        return this._handleCaptchaDisaster(new Error("captcha unavailable"), {
          stage: "scriptLoad",
          errorCode: CAPTCHA_DISASTER_ERROR.JS_LOAD_ERROR.code,
          errorMessage: CAPTCHA_DISASTER_ERROR.JS_LOAD_ERROR.message,
        });
      } catch (error) {
        this.showCaptchaLoading = false;
        return this._handleCaptchaDisaster(error, {
          stage: "showCaptcha",
          errorCode: CAPTCHA_DISASTER_ERROR.JS_LOAD_ERROR.code,
          errorMessage: CAPTCHA_DISASTER_ERROR.JS_LOAD_ERROR.message,
        });
      }
    },
  },
};
